import s from './Calendar.module.scss';
import { Calendar as NewCalendar } from 'react-calendar';
import clsx from 'clsx';
import useGetAvailability from 'sdk/hooks/booking/useGetAvailability';
import Button from 'components/shared/Button';
import React, { useEffect } from 'react';
import 'react-calendar/dist/Calendar.css';
import './newCalendar.css';
import { useTranslation } from 'react-i18next';
import { filterBookedSlots, formatTime, getAvailableTimes, filterEdgeFunctionSlots } from './utilsBookingCalendar';
import useGetAvailableTimeSlots from 'sdk/hooks/booking/useGetAvailableTimeSlots';

interface BookingCalendarProps {
  teamId?: any;
  salonId?: string | number;
  setSelectedDay: (day: any) => void;
  selectedDay: any;
  handleNext: () => void;
  setSelectedTime: any;
  setSelectedEndTime: any;
  selectedTime: any;
  duration: any;
  alreadyBookedTimes?: any;
  selectedServices?: any;
  selectedServiceHistory?: any;
  currentServiceId?: string | null;
}



const BookingCalendar: React.FC<BookingCalendarProps> = ({
  teamId,
  salonId,
  setSelectedDay,
  selectedDay,
  handleNext,
  setSelectedTime,
  setSelectedEndTime,
  selectedTime,
  duration,
  alreadyBookedTimes,
  selectedServices,
  selectedServiceHistory,
  currentServiceId,
}) => {
  const { t } = useTranslation() as any;
  const savedLang = localStorage.getItem('lang') || 'en';
  const [availableDates, setAvailableDates] = React.useState<Set<string>>(new Set());
  const [currentMonth, setCurrentMonth] = React.useState<Date>(new Date());

  const formattedDate = selectedDay
    ? `${selectedDay.year}-${String(selectedDay.month).padStart(2, '0')}-${String(selectedDay.day).padStart(2, '0')}`
    : null;

  const { data: availabilityTimes, refetch } = useGetAvailability(teamId || '', formattedDate);

  const filteredAvailability: any[] = filterBookedSlots(
    availabilityTimes as any[] | undefined,
    alreadyBookedTimes as any[] | undefined,
    formattedDate,
  );

  // Use edge function for proper time slot calculation
  // currentServiceId is explicitly passed from the parent component
  const serviceIdToUse = currentServiceId || (selectedServices?.[0]?.id || selectedServices?.[0]) || (selectedServiceHistory?.[0]?.id || selectedServiceHistory?.[0]) || '';
  
  const { data: edgeFunctionData, refetch: refetchEdgeFunction } = useGetAvailableTimeSlots({
    date: formattedDate,
    team_member_id: teamId || '',
    service_id: serviceIdToUse,
    granularity: 30, // Changed to 30 minutes
    salon_id: salonId || '',
  });

  // Filter edge function results against already booked times in current session
  const availableTimes: any[] = filterEdgeFunctionSlots(
    edgeFunctionData?.slots || [],
    alreadyBookedTimes as any[] | undefined,
    formattedDate,
  );

  const handleSendTime = () => handleNext();

  const handleSelectTime = (start: any, end: any) => {
    setSelectedTime(start);
    setSelectedEndTime(end);
  };

  const handleChange = (value: any) => {
    const date = value instanceof Date ? value : Array.isArray(value) ? value[0] : null;
    if (!date) return;
    setSelectedDay({
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    });
  };

  const handleActiveStartDateChange = ({ activeStartDate }: any) => {
    if (activeStartDate) {
      setCurrentMonth(activeStartDate);
    }
  };

  const tileClassName = ({ date, view }: any) => {
    if (view !== 'month') return null;

    const dateString = date.toISOString().split('T')[0];
    const hasAvailability = availableDates.has(dateString);
    const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

    if (isPast) return null;
    if (!hasAvailability) return s.no_availability;

    return null;
  };

  // Fetch availability for the entire month
  useEffect(() => {
    const fetchMonthAvailability = async () => {
      if (!teamId) return;

      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const startDate = firstDay.toISOString().split('T')[0];
      const endDate = lastDay.toISOString().split('T')[0];

      try {
        const { supabase } = await import('sdk/api');
        const query = supabase
          .from('teams_availability')
          .select('date')
          .gte('date', startDate)
          .lte('date', endDate)
          .eq('is_available', true);

        if (teamId !== 'random') {
          query.eq('team_id', teamId);
        }

        const { data, error } = await query;

        if (!error && data) {
          const dates = new Set(data.map((item: any) => item.date));
          setAvailableDates(dates);
        }
      } catch (error) {
        console.error('Error fetching month availability:', error);
      }
    };

    fetchMonthAvailability();
  }, [teamId, currentMonth]);

  useEffect(() => {
    refetch();
    refetchEdgeFunction();
  }, [selectedDay, teamId, refetch, refetchEdgeFunction]);

  return (
    <div className={s.calendar_container}>
      <div className={s.calendar_content}>
        <NewCalendar
          onChange={handleChange}
          onActiveStartDateChange={handleActiveStartDateChange}
          value={
            selectedDay ? new Date(selectedDay.year, selectedDay.month - 1, selectedDay.day) : null
          }
          locale={savedLang}
          tileClassName={tileClassName}
          minDate={new Date()}
          calendarType="gregory"
        />
        <div className={availableTimes.length ? s.times : ''}>
          {availableTimes.map((time, index) => (
            <div
              key={index}
              className={clsx(s.times_item, selectedTime === time.start_time && s.active_time)}
              onClick={() => handleSelectTime(time.start_time, time.end_time)}
            >
              {`${formatTime(time.start_time, savedLang)} - ${formatTime(time.end_time, savedLang)}`}
            </div>
          ))}
        </div>
        {!availableTimes.length && (
          <div className={s.no_time}>
            <p>{t('There are no available slot for this date')}</p>
          </div>
        )}
      </div>
      <div className={s.button_container}>
        <Button
          className={s.button}
          wide
          darkGreen
          disabled={!selectedTime}
          onClick={handleSendTime}
        >
          {t('Book now')}
        </Button>
      </div>
    </div>
  );
};

export default BookingCalendar;
